import React, { useEffect, useMemo, useState } from "react";
import { Avatar, Box, Button, Chip, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import CloseRoundedIcon      from "@mui/icons-material/CloseRounded";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import DragIndicatorRoundedIcon from "@mui/icons-material/DragIndicatorRounded";
import EmployeeAutocomplete from "../../../component/common/EmployeeAutocomplete";
import ComboBox from "../../../component/common/ComboBox";
import { api } from "../../../util/api";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

/**
 * 결재선·열람자 — 왼쪽에서 고치고 오른쪽에서 완성된 라인을 본다.
 *
 * <h3>기본값을 두는 이유</h3>
 * 예전엔 상신자가 결재자를 맨손으로 골랐다. 그래서 같은 유형인데 단계 수가 제각각이었고
 * (운영 실측: 기타결재 27건은 1단계, 대금지급은 1·2·7단계가 뒤섞여 있었다),
 * 열람 기능이 없어서 <b>보게 하려고 결재선에 사람을 넣는</b> 일까지 벌어졌다.
 * 7단계짜리 지출 결재 한 건이 그 예인데 7명 중 6명이 실제로는 열람자였고,
 * 그 문서는 일주일째 1단계에 멈춰 있었다.
 *
 * 그래서 양식이 «보통은 이렇게» 를 채워 준다. 다만 예외는 늘 생기므로 잠그지는 않는다.
 *
 * <h3>본인은 «중간» 결재자 후보에서만 뺀다</h3>
 * 기안자가 자기 문서의 <b>중간</b> 결재자로 들어가면 본인이 도장을 찍고 다음 사람에게 넘기는 게 되어
 * 결재선을 길게 만드는 것 말고 뜻이 없다. 실제로 대금지급 두 건이 그렇게 올라갔고 한 건은
 * <b>본인 승인만으로 확정 원장</b>까지 갔다. 서버가 상신·승인 양쪽에서 막지만
 * (<code>assertSubmitterNotInLine</code> / <code>assertNotSelfApproval</code>),
 * 400 을 보고 고치게 하는 것보다 <b>애초에 고를 수 없는</b> 쪽이 맞다.
 *
 * <b>최종 결재자 칸은 다르다.</b> 최종결재자로 지정된 사람은 그 유형의 결재권자라, 자기 문서라고
 * 그 자리를 빼면 정작 결재권자인 본인 건만 남에게 가는 모양이 된다(2026-08 결정). 그래서 이 칸만
 * 본인을 남긴다 — 양식 기본값도 마지막 단계의 본인은 스킵하지 않고 그대로 내려온다.
 *
 * <h3>최종 결재자 칸이 따로 있어도 어긋나지 않는 이유</h3>
 * 최종 결재자를 <b>별도 상태로 두지 않는다.</b> 결재자 목록 하나만 있고, 최종 결재자 칸은
 * 그 목록의 <b>마지막 자리</b>를 가리키는 창일 뿐이다. 그 칸에서 사람을 고르면 마지막 자리가
 * 바뀌고, 오른쪽에서 순서를 끌어 옮기면 새로 마지막이 된 사람이 그 칸에 뜬다.
 * 값이 둘이 아니므로 «마지막 결재자 ≠ 최종 승인자» 인 상태를 만들 방법이 없다.
 */
export default function ApprovalLineEditor({
    form, myInfo, approvers, viewers, onChangeApprovers, onChangeViewers, onReset,
}) {
    const skipped = (form?.defaultLine || []).filter((s) => s.skipped);

    const mid   = approvers.slice(0, -1);
    const final = approvers.length ? approvers[approvers.length - 1] : null;

    const setFinal = (person) => onChangeApprovers(person ? [...mid, person] : mid);
    const addMid   = (person) => onChangeApprovers([...mid, person, ...(final ? [final] : [])]);
    const removeAt = (id)     => onChangeApprovers(approvers.filter((x) => x.id !== id));

    const reorder = (from, to) => {
        if (from === to || from == null || to == null) return;
        const next = [...approvers];
        const [it] = next.splice(from, 1);
        next.splice(to, 0, it);
        onChangeApprovers(next);
    };

    const toPerson = (emp) => ({
        id: emp.id,
        name: emp.name,
        position: emp.position?.positionName ?? emp.positionName,
        deptName: emp.department?.deptName ?? emp.deptName,
    });

    /**
     * 팀 단위 열람자 추가 — 한 명씩 치는 것이 실제로 막히던 자리다.
     *
     * <p>경영지원부 전원을 열람자로 넣으려면 이름을 다섯 번 쳐야 했고, 한 명이 빠져도 아무도
     * 모른다. 팀을 고르면 그 팀 재직자가 한 번에 들어간다.
     *
     * <h3>왜 «부서» 를 저장하지 않고 사람으로 펼치는가</h3>
     * <p>열람자는 사람 id 목록으로 저장된다. 부서를 통째로 걸어 두면 «지금 그 부서인 사람» 이
     * 시점마다 달라져, 반년 뒤에 문서를 열었을 때 당시에 못 보던 사람이 열람자로 보인다.
     * 펼쳐서 넣으면 그 문서의 열람자는 상신 시점 명단으로 고정된다.
     *
     * <p>명부는 {@code GET /api/employees} 다 — 권한 없이 부르는 재직자 공용 명부라 부서까지 함께
     * 온다. 인사 관리용 {@code /api/employees/list} 를 쓰면 권한 없는 기안자 화면이 통째로 403 이 된다.
     */
    const [staff, setStaff] = useState([]);
    useEffect(() => {
        let alive = true;
        api.get(`${API_BASE}/employees`)
            .then((res) => {
                const data = res?.data ?? res ?? [];
                if (alive) setStaff(Array.isArray(data) ? data : []);
            })
            // 실패해도 화면은 그대로 쓸 수 있어야 한다 — 팀 추가만 빠지고 한 명씩 넣는 길은 남는다.
            .catch(() => { if (alive) setStaff([]); });
        return () => { alive = false; };
    }, []);

    /** 부서명 → 그 부서 재직자. 부서가 없는 직원은 묶을 기준이 없으므로 뺀다. */
    const staffByDept = useMemo(() => {
        const map = new Map();
        for (const e of staff) {
            const dept = e.department?.deptName ?? e.deptName;
            if (!dept) continue;
            if (!map.has(dept)) map.set(dept, []);
            map.get(dept).push(e);
        }
        return map;
    }, [staff]);

    /**
     * 팀 전원을 열람자로. 이미 열람자이거나 결재자인 사람은 조용히 건너뛴다.
     *
     * <p>결재자를 빼는 이유는 서버가 어차피 겹치는 열람자를 저장하지 않기 때문이다
     * (같은 문서가 결재함과 열람함에 동시에 뜨는 것을 막는다). 화면에서 미리 빼 두지 않으면
     * 칩으로는 들어갔는데 저장하면 사라지는, 설명하기 어려운 차이가 생긴다.
     */
    const addDeptViewers = (deptName) => {
        const members = staffByDept.get(deptName) || [];
        const approverIds = new Set(approvers.map((a) => a.id));
        const already = new Set(viewers.map((v) => v.employeeId));

        const added = members
            .filter((e) => !approverIds.has(e.id) && !already.has(e.id))
            .map((e) => ({
                employeeId: e.id, name: e.name,
                deptName: e.department?.deptName ?? e.deptName,
            }));
        if (added.length) onChangeViewers([...viewers, ...added]);

        // 결과를 한 줄로 알린다.
        //
        // 이미 전원이 열람자인 팀을 고르면 새로 넣을 사람이 없어 «화면이 아무것도 안 바뀐다».
        // 그러면 고른 사람은 «안 먹혔나» 하고 한 번 더 누른다. 실제로는 그 팀 전원이 열람자인
        // 상태가 맞으므로, 몇 명이 새로 들어갔는지가 아니라 <b>지금 몇 명이 들어가 있는지</b>를 말한다.
        const inViewers = members.filter((e) => !approverIds.has(e.id)).length;
        const skipped = members.length - inViewers;
        setDeptResult(
            `${deptName} ${inViewers}명을 열람자에 추가했습니다.`
            + (skipped > 0 ? ` (결재자 ${skipped}명 제외)` : ""));
    };

    /**
     * 팀 선택지 — 라벨에 <b>이름을 전부</b> 적는다.
     *
     * <p>«기획1팀 (4명)» 은 몇 명인지는 알려 주지만 <b>누가</b> 들어오는지는 알려 주지 않는다.
     * 열람자는 문서를 볼 사람을 정하는 일이라 인원수보다 이름이 중요하다 — 넣고 나서 칩을 세어
     * 보는 것보다 고르기 전에 보이는 편이 낫다.
     *
     * <p>라벨에 이름을 넣으면 검색도 따라온다. 자동완성은 라벨로 거르므로 «노예원» 을 치면
     * 그 사람이 속한 팀이 떠오른다 — 어느 팀인지 모를 때 실제로 이렇게 찾는다.
     */
    const deptOptions = useMemo(() => (
        [...staffByDept.entries()]
            .map(([dept, members]) => ({
                value: dept,
                label: `${dept} · ${members.map((e) => e.name).join(", ")}`,
                names: members.map((e) => e.name),
            }))
            .sort((a, b) => a.value.localeCompare(b.value, "ko"))
    ), [staffByDept]);

    /**
     * 고른 뒤 입력칸을 비운다.
     *
     * <p>MUI 는 선택하면 입력칸을 그 라벨로 채운다({@code reason === "reset"}). 그대로 두면
     * 이름이 줄줄이 남아 «아직 안 들어갔나» 로 읽히고, 다음 팀을 고르려면 지우고 시작해야 한다.
     * 이 칸은 값을 담는 곳이 아니라 <b>버튼</b>에 가깝다.
     */
    const [deptInput, setDeptInput] = useState("");

    /** 마지막 팀 추가 결과 한 줄. 다음 선택에서 덮어쓴다. */
    const [deptResult, setDeptResult] = useState("");

    return (
        <Box>
            <Stack direction="row" alignItems="center" sx={{ mt: 1, mb: 1 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#334155" }}>결재선</Typography>
                <Box sx={{ flex: 1 }} />
                <Button size="small" startIcon={<RestartAltRoundedIcon sx={{ fontSize: 15 }} />}
                        onClick={onReset} sx={{ fontSize: 11.5, color: "#64748b" }}>
                    기본값으로
                </Button>
            </Stack>

            <Stack direction="row" spacing={2} alignItems="flex-start">
                {/* ── 왼쪽: 고치는 곳 ── */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <FieldLabel>중간 결재자</FieldLabel>
                    {mid.length > 0 && (
                        <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mb: 0.75 }}>
                            {mid.map((p, i) => (
                                <Chip
                                    key={p.id} size="small"
                                    avatar={<Avatar sx={{ bgcolor: "#2563eb", fontSize: 10 }}>{(p.name || "?")[0]}</Avatar>}
                                    label={`${i + 1}. ${p.name}${p.deptName ? ` · ${p.deptName}` : ""}`}
                                    onDelete={() => removeAt(p.id)}
                                    sx={{ fontSize: 11.5, bgcolor: "#eff6ff", color: "#1e40af" }}
                                />
                            ))}
                        </Stack>
                    )}
                    <EmployeeAutocomplete
                        value={null}
                        onChange={(emp) => {
                            if (!emp || typeof emp === "string") return;
                            if (approvers.some((x) => x.id === emp.id)) return;
                            addMid(toPerson(emp));
                        }}
                        excludeIds={approvers.map((x) => x.id)}
                        excludeSelf selfId={myInfo?.id}
                        placeholder="중간 결재자 추가 (순서대로 쌓입니다)"
                        variant="outlined"
                        disableUnderline={false}
                        // 이 칸들은 모달 아래쪽에 있어 기본 320px 목록이 창 밖으로 넘쳐 끝이 잘린다.
                        dropdownMaxHeight={220}
                    />

                    <FieldLabel sx={{ mt: 2 }}>최종 결재자</FieldLabel>
                    <EmployeeAutocomplete
                        value={final}
                        onChange={(emp) => {
                            if (!emp || typeof emp === "string") { setFinal(null); return; }
                            if (mid.some((x) => x.id === emp.id)) return;   // 중간 결재자와 겹치면 무시
                            setFinal(toPerson(emp));
                        }}
                        excludeIds={mid.map((x) => x.id)}
                        // 본인을 빼지 않는다 — 최종 결재자 자리만은 본인이 설 수 있다(2026-08 결정).
                        // 중간 칸은 계속 뺀다. 서버도 «중간 자리의 본인» 만 400 으로 막는다.
                        placeholder="최종 결재자 선택"
                        variant="outlined"
                        disableUnderline={false}
                        // 이 칸들은 모달 아래쪽에 있어 기본 320px 목록이 창 밖으로 넘쳐 끝이 잘린다.
                        dropdownMaxHeight={220}
                    />
                    {!final && (
                        <Typography sx={{ fontSize: 11.5, color: "#b45309", mt: 0.5 }}>
                            최종 결재자가 없습니다. 한 명을 지정해 주세요.
                        </Typography>
                    )}

                    {skipped.map((s) => (
                        <Typography key={s.stepOrder} sx={{ fontSize: 11, color: "#94a3b8", mt: 0.5 }}>
                            · 기본값 {s.stepOrder}단계 건너뜀 — {s.skipReason}
                        </Typography>
                    ))}

                    <FieldLabel sx={{ mt: 2 }}>열람자</FieldLabel>
                    {viewers.length === 0 ? (
                        <Typography sx={{ fontSize: 12, color: "#94a3b8", mb: 0.75 }}>지정된 열람자가 없습니다.</Typography>
                    ) : (
                        <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mb: 0.75 }}>
                            {viewers.map((v) => {
                                // 서버는 결재자와 겹치는 열람자를 빼고 저장한다(같은 문서가 결재함·열람함에
                                // 두 번 뜨는 걸 막으려고). 화면이 안 보여주면 올린 뒤 조용히 사라진 것처럼 보인다.
                                const isApprover = approvers.some((a) => a.id === v.employeeId);
                                return (
                                    <Tooltip key={v.employeeId}
                                             title={isApprover ? "결재자로 지정돼 열람자에서는 빠집니다" : ""}
                                             placement="top">
                                        <Chip
                                            size="small"
                                            label={`${v.name}${v.deptName ? ` · ${v.deptName}` : ""}`}
                                            onDelete={() => onChangeViewers(viewers.filter((x) => x.employeeId !== v.employeeId))}
                                            sx={{ fontSize: 11, bgcolor: "#f1f5f9",
                                                  opacity: isApprover ? 0.5 : 1,
                                                  textDecoration: isApprover ? "line-through" : "none" }}
                                        />
                                    </Tooltip>
                                );
                            })}
                        </Stack>
                    )}
                    <EmployeeAutocomplete
                        value={null}
                        onChange={(emp) => {
                            if (!emp || typeof emp === "string") return;
                            if (viewers.some((x) => x.employeeId === emp.id)) return;
                            onChangeViewers([...viewers, {
                                employeeId: emp.id, name: emp.name,
                                deptName: emp.department?.deptName ?? emp.deptName,
                            }]);
                        }}
                        excludeIds={[...viewers.map((x) => x.employeeId), ...approvers.map((a) => a.id)]}
                        placeholder="열람자 추가"
                        variant="outlined"
                        disableUnderline={false}
                        // 이 칸들은 모달 아래쪽에 있어 기본 320px 목록이 창 밖으로 넘쳐 끝이 잘린다.
                        dropdownMaxHeight={220}
                    />

                    {/* 팀 단위 추가 — 고르면 그 팀 전원이 칩으로 펼쳐진다. 명부를 못 받았으면 감춘다. */}
                    {staffByDept.size > 0 && (
                        <Box sx={{ mt: 1 }}>
                            <ComboBox
                                options={deptOptions}
                                value=""
                                onChange={(dept) => { if (dept) addDeptViewers(dept); setDeptInput(""); }}
                                placeholder="팀으로 추가 (팀 전원)"
                                inputValue={deptInput}
                                // «reset» 은 무시한다 — 선택 직후 MUI 가 입력칸을 그 옵션 라벨로 채우려는 신호다.
                                // 그대로 두면 «경영본부 · 김한새» 가 남고, 그 텍스트가 다음 번에 <b>필터</b>로
                                // 작동해 다른 팀이 목록에서 사라진다(한 번 고르면 두 번째를 못 고른다).
                                // 비우는 것은 위 onChange 가 맡고, 여기서는 사람이 친 것만 받는다.
                                onInputChange={(_, v, reason) => {
                                    if (reason === "input") setDeptInput(v);
                                    else if (reason === "clear") setDeptInput("");
                                }}
                                // 목록 높이를 묶어 둔다.
                                //
                                // 이 칸은 모달 아래쪽에 있어서 MUI 가 목록을 <b>위로</b> 펼친다. 항목이 두 줄
                                // (팀명 + 이름들)이라 16개 팀이면 목록이 화면을 채우고, 바로 위에 있는
                                // «열람자» 칩이 통째로 가려진다 — 팀을 여러 번 넣을 때 방금 누른 결과가
                                // 안 보이는 게 그래서다. 4~5개만 보이면 칩이 반쯤은 남는다.
                                ListboxProps={{ style: { maxHeight: 230 } }}
                                // 목록은 두 줄로 — 팀 이름과 이름 목록을 한 줄에 이으면 어디까지가 팀명인지
                                // 눈이 매번 «·» 를 찾아야 한다. 이름은 줄바꿈을 허용해 잘리지 않게 둔다.
                                renderOption={(props, o) => (
                                    <li {...props} key={o.value}>
                                        <Box sx={{ py: 0.3 }}>
                                            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#334155" }}>
                                                {o.value}
                                                <Box component="span" sx={{ ml: 0.6, fontWeight: 400, color: "#94a3b8" }}>
                                                    {o.names.length}명
                                                </Box>
                                            </Typography>
                                            <Typography sx={{ fontSize: 11.5, color: "#64748b", whiteSpace: "normal" }}>
                                                {o.names.join(", ")}
                                            </Typography>
                                        </Box>
                                    </li>
                                )}
                            />
                        </Box>
                    )}

                    {deptResult && (
                        <Typography sx={{ fontSize: 11.5, color: "#15803d", mt: 0.75, fontWeight: 600 }}>
                            {deptResult}
                        </Typography>
                    )}

                    <Typography sx={{ fontSize: 11, color: "#94a3b8", mt: 0.75 }}>
                        열람자는 결재하지 않고 문서를 보고 댓글만 남깁니다.
                        팀으로 추가하면 그 팀 재직자가 한 번에 들어갑니다.
                    </Typography>
                </Box>

                {/* ── 오른쪽: 완성된 라인 ── */}
                <LinePreview
                    submitter={myInfo}
                    approvers={approvers}
                    onReorder={reorder}
                    onRemove={removeAt}
                />
            </Stack>
        </Box>
    );
}

/**
 * 실제로 만들어질 결재 라인 — 상세 화면과 같은 세로 타임라인.
 *
 * 끌어서 순서를 바꿀 수 있다. 맨 아래로 옮긴 사람이 최종 결재자가 된다 —
 * 왼쪽 «최종 결재자» 칸도 같이 바뀐다. 값이 하나뿐이라 따로 맞출 게 없다.
 */
function LinePreview({ submitter, approvers, onReorder, onRemove }) {
    const [dragIdx, setDragIdx] = useState(null);
    const [overIdx, setOverIdx] = useState(null);

    return (
        <Box sx={{ width: 268, flexShrink: 0, border: "1px solid #e2e8f0",
                   borderRadius: 2, overflow: "hidden", alignSelf: "stretch" }}>
            <Box sx={{ px: 1.5, py: 1, bgcolor: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                <Typography sx={{ fontSize: 12, fontWeight: 800, color: "#334155" }}>결재 라인</Typography>
            </Box>

            <Box sx={{ p: 1.5 }}>
                <Row avatarBg="#2563eb" initial={(submitter?.name || "?")[0]}
                     name={submitter?.name || "나"}
                     sub={[submitter?.positionName ?? submitter?.position?.positionName,
                           submitter?.deptName ?? submitter?.department?.deptName].filter(Boolean).join(" · ")}
                     state="신청" stateColor="#2563eb" connect={approvers.length > 0} />

                {approvers.map((p, i) => {
                    const last = i === approvers.length - 1;
                    return (
                        <Box
                            key={p.id}
                            draggable
                            onDragStart={() => setDragIdx(i)}
                            onDragEnd={() => { setDragIdx(null); setOverIdx(null); }}
                            onDragOver={(e) => { e.preventDefault(); setOverIdx(i); }}
                            onDrop={(e) => { e.preventDefault(); onReorder(dragIdx, i); setDragIdx(null); setOverIdx(null); }}
                            sx={{
                                cursor: "grab",
                                borderRadius: 1,
                                opacity: dragIdx === i ? 0.4 : 1,
                                bgcolor: overIdx === i && dragIdx !== i ? "#eff6ff" : "transparent",
                                "&:hover .drag-handle": { opacity: 1 },
                            }}>
                            <Row
                                avatarBg="#94a3b8" initial={(p.name || "?")[0]}
                                name={p.name}
                                sub={[p.position, p.deptName].filter(Boolean).join(" · ")}
                                state={`${i + 1}단계 결재`} stateColor="#64748b"
                                connect={!last}
                                badge={last ? "최종" : null}
                                onRemove={() => onRemove(p.id)}
                            />
                        </Box>
                    );
                })}

                {approvers.length === 0 && (
                    <Typography sx={{ fontSize: 11.5, color: "#b45309", mt: 1 }}>
                        결재자가 없어 상신할 수 없습니다.
                    </Typography>
                )}

                {approvers.length > 1 && (
                    <Typography sx={{ fontSize: 10.5, color: "#94a3b8", mt: 1.5 }}>
                        끌어서 순서를 바꿀 수 있습니다. 맨 아래가 최종 결재자입니다.
                    </Typography>
                )}
            </Box>
        </Box>
    );
}

function Row({ avatarBg, initial, name, sub, state, stateColor, connect, badge, onRemove }) {
    return (
        <Stack direction="row" spacing={1.25} alignItems="flex-start">
            <Stack alignItems="center" sx={{ flexShrink: 0 }}>
                <Avatar sx={{ width: 30, height: 30, fontSize: 12, bgcolor: avatarBg }}>{initial}</Avatar>
                {connect && <Box sx={{ width: 2, flex: 1, minHeight: 22, bgcolor: "#dbeafe", my: 0.4 }} />}
            </Stack>

            <Box sx={{ flex: 1, minWidth: 0, pb: connect ? 0 : 0.5 }}>
                <Stack direction="row" alignItems="center" spacing={0.6}>
                    {/* 이름이 줄 맨 앞에 오도록 손잡이는 칩 뒤로 뺀다.
                        앞에 두면 사람마다 이름 시작 위치가 어긋나 목록이 들쭉날쭉해 보인다. */}
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }} noWrap>{name}</Typography>
                    {badge && (
                        <Chip size="small" label={badge}
                              sx={{ height: 17, fontSize: 10, fontWeight: 800,
                                    bgcolor: "#fef3c7", color: "#b45309" }} />
                    )}
                    {onRemove && (
                        <DragIndicatorRoundedIcon className="drag-handle"
                            sx={{ fontSize: 14, color: "#cbd5e1", opacity: 0, transition: "opacity 120ms" }} />
                    )}
                    <Box sx={{ flex: 1 }} />
                    {onRemove && (
                        <IconButton size="small" sx={{ p: 0.15, color: "#cbd5e1" }} onClick={onRemove}>
                            <CloseRoundedIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                    )}
                </Stack>
                {sub && <Typography sx={{ fontSize: 11, color: "#94a3b8" }} noWrap>{sub}</Typography>}
                <Typography sx={{ fontSize: 11.5, color: stateColor, fontWeight: 600, mt: 0.15 }}>
                    ● {state}
                </Typography>
            </Box>
        </Stack>
    );
}

function FieldLabel({ children, sx }) {
    return (
        <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: "#475569", mb: 0.6, ...sx }}>
            {children}
        </Typography>
    );
}
